// ./src/components/generic/DateRangeFilter/DateRangeFilter.tsx
import React, { useState, useMemo } from "react";
import {
  Popover,
  Button,
  Stack,
  Group,
  Text,
  Divider,
  UnstyledButton,
  Box,
  Drawer,
  ScrollArea,
  useMantineTheme,
} from "@mantine/core";
import { DatePicker, DatesRangeValue } from "@mantine/dates";
import dayjs from "dayjs";

import { useAppEnvironment } from "@/contexts/AppEnvironmentContext";

import { AppIcon } from "../AppIcon";

// ============================================
// Types
// ============================================

export interface DateRange {
  from: Date | null;
  to: Date | null;
}

export interface DateRangeFilterProps {
  /** Current date range value */
  value: DateRange;
  /** Callback when date range changes */
  onChange: (range: DateRange) => void;
  /** Whether the filter is disabled */
  disabled?: boolean;
  /** Size of the control */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  /** Placeholder text when no date is selected */
  placeholder?: string;
  /** Whether a date range is required (hides clear option) */
  required?: boolean;
  /** Whether dropdown should render in a portal (default: true) */
  withinPortal?: boolean;
  /**
   * Layout mode:
   * - "auto"     — mobile uses bottom-sheet drawer, desktop uses popover (default)
   * - "stacked"  — inline stacked layout: presets row + single-month calendar below.
   *                Ideal for use inside drawers/panels where a popover won't fit.
   * - "popover"  — always renders the desktop popover layout
   */
  layout?: "auto" | "stacked" | "popover";
}

// ============================================
// Preset Definitions
// ============================================

interface DatePreset {
  label: string;
  shortLabel?: string;
  getValue: () => DateRange;
}

const getDatePresets = (): DatePreset[] => {
  const today = dayjs().startOf("day");

  return [
    {
      label: "Today",
      getValue: () => ({
        from: today.toDate(),
        to: today.endOf("day").toDate(),
      }),
    },
    {
      label: "Yesterday",
      getValue: () => ({
        from: today.subtract(1, "day").toDate(),
        to: today.subtract(1, "day").endOf("day").toDate(),
      }),
    },
    {
      label: "Last 7 Days",
      shortLabel: "7 Days",
      getValue: () => ({
        from: today.subtract(6, "day").toDate(),
        to: today.endOf("day").toDate(),
      }),
    },
    {
      label: "Last 30 Days",
      shortLabel: "30 Days",
      getValue: () => ({
        from: today.subtract(29, "day").toDate(),
        to: today.endOf("day").toDate(),
      }),
    },
    {
      label: "This Week",
      getValue: () => ({
        from: today.startOf("week").toDate(),
        to: today.endOf("week").toDate(),
      }),
    },
    {
      label: "Last Week",
      getValue: () => ({
        from: today.subtract(1, "week").startOf("week").toDate(),
        to: today.subtract(1, "week").endOf("week").toDate(),
      }),
    },
    {
      label: "This Month",
      getValue: () => ({
        from: today.startOf("month").toDate(),
        to: today.endOf("month").toDate(),
      }),
    },
    {
      label: "Last Month",
      getValue: () => ({
        from: today.subtract(1, "month").startOf("month").toDate(),
        to: today.subtract(1, "month").endOf("month").toDate(),
      }),
    },
    {
      label: "This Year",
      getValue: () => ({
        from: today.startOf("year").toDate(),
        to: today.endOf("year").toDate(),
      }),
    },
    {
      label: "Last Year",
      getValue: () => ({
        from: today.subtract(1, "year").startOf("year").toDate(),
        to: today.subtract(1, "year").endOf("year").toDate(),
      }),
    },
  ];
};

// ============================================
// Helper Functions
// ============================================

const formatDateRange = (range: DateRange): string => {
  if (!range.from && !range.to) {
    return "";
  }

  const fromStr = range.from ? dayjs(range.from).format("MMM D, YYYY") : "";
  const toStr = range.to ? dayjs(range.to).format("MMM D, YYYY") : "";

  if (fromStr === toStr) {
    return fromStr;
  }

  if (fromStr && toStr) {
    const fromYear = dayjs(range.from).year();
    const toYear = dayjs(range.to).year();

    if (fromYear === toYear) {
      return `${dayjs(range.from).format("MMM D")} - ${dayjs(range.to).format(
        "MMM D, YYYY"
      )}`;
    }

    return `${fromStr} - ${toStr}`;
  }

  return fromStr || toStr;
};

const formatDateRangeShort = (range: DateRange): string => {
  if (!range.from && !range.to) {
    return "";
  }

  const fromStr = range.from ? dayjs(range.from).format("MMM D") : "";
  const toStr = range.to ? dayjs(range.to).format("MMM D") : "";

  if (fromStr === toStr) {
    return fromStr;
  }

  if (fromStr && toStr) {
    return `${fromStr} - ${toStr}`;
  }

  return fromStr || toStr;
};

const findMatchingPreset = (range: DateRange): string | null => {
  if (!range.from || !range.to) return null;

  const presets = getDatePresets();
  const from = dayjs(range.from).startOf("day");
  const to = dayjs(range.to).startOf("day");

  for (const preset of presets) {
    const presetRange = preset.getValue();
    const presetFrom = dayjs(presetRange.from).startOf("day");
    const presetTo = dayjs(presetRange.to).startOf("day");

    if (from.isSame(presetFrom) && to.isSame(presetTo)) {
      return preset.label;
    }
  }

  return null;
};

const dateToPickerValue = (date: Date | null): Date | null => {
  return date;
};

const parsePickerValue = (value: Date | string | null): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.toDate() : null;
};

// ============================================
// Preset Pill Component (Mobile / Stacked)
// ============================================

interface PresetPillProps {
  preset: DatePreset;
  isActive: boolean;
  onClick: () => void;
}

const PresetPill: React.FC<PresetPillProps> = ({
  preset,
  isActive,
  onClick,
}) => {
  const theme = useMantineTheme();

  return (
    <UnstyledButton
      onClick={onClick}
      py={8}
      px={14}
      style={{
        borderRadius: 20,
        backgroundColor: isActive
          ? theme.colors.brand[5]
          : theme.colors.gray[1],
        color: isActive ? "white" : theme.colors.dark[6],
        fontWeight: isActive ? 600 : 500,
        fontSize: 13,
        whiteSpace: "nowrap",
        transition: "all 150ms ease",
      }}
    >
      {preset.shortLabel || preset.label}
    </UnstyledButton>
  );
};

// ============================================
// Stacked Layout (inline — no trigger button)
// Used inside drawers/panels on any screen size.
// ============================================

interface StackedDateRangeProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  required?: boolean;
  placeholder?: string;
}

const StackedDateRange: React.FC<StackedDateRangeProps> = ({
  value,
  onChange,
  required = false,
}) => {
  const theme = useMantineTheme();

  const presets = useMemo(() => getDatePresets(), []);
  const hasValue = value.from !== null || value.to !== null;

  const [tempRange, setTempRange] = useState<[Date | null, Date | null]>([
    value.from,
    value.to,
  ]);

  // Keep tempRange in sync when value changes externally (e.g. preset click)
  React.useEffect(() => {
    setTempRange([value.from, value.to]);
  }, [value.from, value.to]);

  const isPresetActive = (preset: DatePreset): boolean => {
    if (!value.from || !value.to) return false;
    const presetRange = preset.getValue();
    return (
      dayjs(value.from)
        .startOf("day")
        .isSame(dayjs(presetRange.from).startOf("day")) &&
      dayjs(value.to)
        .startOf("day")
        .isSame(dayjs(presetRange.to).startOf("day"))
    );
  };

  const handlePresetClick = (preset: DatePreset) => {
    const range = preset.getValue();
    onChange(range);
  };

  const handleClear = () => {
    onChange({ from: null, to: null });
  };

  const handleDateChange = (dates: DatesRangeValue) => {
    const fromDate = parsePickerValue(dates[0]);
    const toDate = parsePickerValue(dates[1]);

    setTempRange([fromDate, toDate]);

    if (fromDate && toDate) {
      onChange({
        from: dayjs(fromDate).startOf("day").toDate(),
        to: dayjs(toDate).endOf("day").toDate(),
      });
    }
  };

  const pickerValue: [Date | null, Date | null] = [
    dateToPickerValue(tempRange[0]),
    dateToPickerValue(tempRange[1]),
  ];

  // Summary badge shown above calendar when a range is active
  const summaryLabel = useMemo(() => {
    const preset = findMatchingPreset(value);
    if (preset) return preset;
    return formatDateRange(value);
  }, [value]);

  return (
    <Stack gap="sm">
      {/* Active range summary */}
      {hasValue && (
        <Box
          py={8}
          px={12}
          style={{
            borderRadius: "var(--mantine-radius-sm)",
            backgroundColor: theme.colors.brand[0],
            border: `1px solid ${theme.colors.brand[3]}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Group gap="xs">
            <AppIcon iconName="Calendar" size={14} color={theme.colors.brand[6]} />
            <Text size="sm" fw={600} c="brand">
              {summaryLabel}
            </Text>
          </Group>
          {!required && (
            <UnstyledButton
              onClick={handleClear}
              style={{ display: "flex", alignItems: "center" }}
            >
              <AppIcon iconName="X" size={14} color={theme.colors.brand[6]} />
            </UnstyledButton>
          )}
        </Box>
      )}

      {/* Quick Presets — horizontally scrollable pills */}
      <ScrollArea type="never" offsetScrollbars={false}>
        <Group gap={6} wrap="nowrap" pb={2}>
          {!required && (
            <PresetPill
              preset={{
                label: "All Time",
                getValue: () => ({ from: null, to: null }),
              }}
              isActive={!hasValue}
              onClick={handleClear}
            />
          )}
          {presets.map((preset) => (
            <PresetPill
              key={preset.label}
              preset={preset}
              isActive={isPresetActive(preset)}
              onClick={() => handlePresetClick(preset)}
            />
          ))}
        </Group>
      </ScrollArea>

      <Divider />

      {/* Single-column calendar — always fits in a drawer */}
      <Box style={{ display: "flex", justifyContent: "center" }}>
        <DatePicker
          type="range"
          value={pickerValue}
          onChange={handleDateChange}
          numberOfColumns={1}
          maxDate={new Date()}
          size="sm"
          styles={{
            month: { width: "100%" },
            calendarHeader: { maxWidth: "100%" },
            day: { height: 36, fontSize: 14 },
            weekday: { fontSize: 12, padding: "6px 0" },
          }}
        />
      </Box>

      {/* Hint when first date is picked */}
      {tempRange[0] && !tempRange[1] && (
        <Text size="xs" c="dimmed" ta="center">
          Select end date
        </Text>
      )}
    </Stack>
  );
};

// ============================================
// Component
// ============================================

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  value,
  onChange,
  disabled = false,
  size = "sm",
  placeholder = "All Time",
  required = false,
  withinPortal = true,
  layout = "auto",
}) => {
  const { isMobile } = useAppEnvironment();

  const [opened, setOpened] = useState(false);
  const [tempRange, setTempRange] = useState<[Date | null, Date | null]>([
    value.from,
    value.to,
  ]);

  const presets = useMemo(() => getDatePresets(), []);

  // ============================================
  // Stacked layout — render inline, no trigger
  // ============================================

  if (layout === "stacked") {
    return (
      <StackedDateRange
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
      />
    );
  }

  // ============================================
  // Auto / Popover — needs trigger button
  // ============================================

  const displayText = useMemo(() => {
    const matchingPreset = findMatchingPreset(value);
    if (matchingPreset) return matchingPreset;
    const formatted = formatDateRange(value);
    return formatted || placeholder;
  }, [value, placeholder]);

  const hasValue = value.from !== null || value.to !== null;

  const isPresetActive = (preset: DatePreset): boolean => {
    if (!value.from || !value.to) return false;
    const presetRange = preset.getValue();
    return (
      dayjs(value.from)
        .startOf("day")
        .isSame(dayjs(presetRange.from).startOf("day")) &&
      dayjs(value.to)
        .startOf("day")
        .isSame(dayjs(presetRange.to).startOf("day"))
    );
  };

  const handlePresetClick = (preset: DatePreset) => {
    const range = preset.getValue();
    onChange(range);
    setTempRange([range.from, range.to]);
    if (!isMobile) {
      setOpened(false);
    }
  };

  const handleDateChange = (dates: DatesRangeValue) => {
    const fromDate = parsePickerValue(dates[0]);
    const toDate = parsePickerValue(dates[1]);

    setTempRange([fromDate, toDate]);

    if (fromDate && toDate) {
      onChange({
        from: dayjs(fromDate).startOf("day").toDate(),
        to: dayjs(toDate).endOf("day").toDate(),
      });
      if (!isMobile) {
        setOpened(false);
      }
    }
  };

  const handleClear = () => {
    onChange({ from: null, to: null });
    setTempRange([null, null]);
    setOpened(false);
  };

  const handleApply = () => {
    setOpened(false);
  };

  const handleOpen = () => {
    setTempRange([value.from, value.to]);
    setOpened(true);
  };

  const pickerValue: [Date | null, Date | null] = [
    dateToPickerValue(tempRange[0]),
    dateToPickerValue(tempRange[1]),
  ];

  const triggerButton = (
    <Button
      variant={hasValue ? "light" : "default"}
      color={hasValue ? "brand" : undefined}
      size={size}
      onClick={handleOpen}
      disabled={disabled}
      rightSection={
        hasValue && !required ? (
          <Box
            component="span"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            style={{ display: "flex", cursor: "pointer" }}
          >
            <AppIcon iconName="X" size={14} />
          </Box>
        ) : (
          <AppIcon iconName="ChevronDown" size={14} />
        )
      }
      leftSection={<AppIcon iconName="Calendar" size={14} />}
      styles={{
        root: {
          fontWeight: 400,
        },
      }}
    >
      {displayText}
    </Button>
  );

  // ============================================
  // Mobile Layout (Bottom Sheet)
  // ============================================

  if (layout === "auto" && isMobile) {
    return (
      <>
        {triggerButton}

        <Drawer
          opened={opened}
          onClose={() => setOpened(false)}
          position="bottom"
          size="auto"
          title={
            <Group justify="space-between" w="100%" pr="md">
              <Text fw={600} size="lg">
                Select Date Range
              </Text>
              {hasValue && (
                <Text size="sm" c="brand" fw={500}>
                  {formatDateRangeShort(value)}
                </Text>
              )}
            </Group>
          }
          styles={{
            content: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            },
            header: {
              paddingBottom: 8,
            },
            title: {
              width: "100%",
            },
            body: {
              paddingTop: 0,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            },
          }}
        >
          <Stack gap="md" style={{ flex: 1, overflow: "hidden" }}>
            {/* Quick Presets - Horizontal Scrollable */}
            <Box>
              <Text size="sm" fw={500} c="dimmed" mb={8}>
                Quick Select
              </Text>
              <ScrollArea type="never" offsetScrollbars={false}>
                <Group gap="xs" wrap="nowrap" pb={4}>
                  {!required && (
                    <PresetPill
                      preset={{
                        label: "All Time",
                        getValue: () => ({ from: null, to: null }),
                      }}
                      isActive={!hasValue}
                      onClick={handleClear}
                    />
                  )}
                  {presets.map((preset) => (
                    <PresetPill
                      key={preset.label}
                      preset={preset}
                      isActive={isPresetActive(preset)}
                      onClick={() => handlePresetClick(preset)}
                    />
                  ))}
                </Group>
              </ScrollArea>
            </Box>

            <Divider />

            {/* Calendar - Single Column for Mobile */}
            <Box style={{ flex: 1, overflow: "auto" }}>
              <Text size="sm" fw={500} c="dimmed" mb={8}>
                Custom Range
              </Text>
              <Box
                style={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <DatePicker
                  type="range"
                  value={pickerValue}
                  onChange={handleDateChange}
                  numberOfColumns={1}
                  maxDate={new Date()}
                  size="md"
                  styles={{
                    month: {
                      width: "100%",
                    },
                    calendarHeader: {
                      maxWidth: "100%",
                    },
                    day: {
                      width: 44,
                      height: 44,
                      fontSize: 16,
                    },
                    weekday: {
                      fontSize: 14,
                      padding: "8px 0",
                    },
                    monthCell: {
                      padding: 2,
                    },
                  }}
                />
              </Box>
              {tempRange[0] && !tempRange[1] && (
                <Text size="sm" c="dimmed" ta="center" mt="sm">
                  Select end date
                </Text>
              )}
            </Box>

            <Divider />

            {/* Action Buttons */}
            <Group gap="sm" grow pb={20}>
              {!required && hasValue && (
                <Button variant="light" color="gray" onClick={handleClear}>
                  Clear
                </Button>
              )}
              <Button onClick={handleApply}>Done</Button>
            </Group>
          </Stack>
        </Drawer>
      </>
    );
  }

  // ============================================
  // Desktop Layout (Popover) — layout="popover" or layout="auto" on desktop
  // ============================================

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      shadow="md"
      withinPortal={withinPortal}
    >
      <Popover.Target>{triggerButton}</Popover.Target>

      <Popover.Dropdown p={0}>
        <Group align="flex-start" gap={0} wrap="nowrap">
          {/* Presets column */}
          <Stack
            gap={0}
            p="xs"
            style={{
              borderRight: "1px solid var(--mantine-color-default-border)",
            }}
          >
            <Text size="xs" c="dimmed" fw={500} mb={4} px="xs">
              Quick Select
            </Text>
            {presets.map((preset) => {
              const isActive = isPresetActive(preset);

              return (
                <UnstyledButton
                  key={preset.label}
                  onClick={() => handlePresetClick(preset)}
                  px="xs"
                  py={6}
                  style={{
                    borderRadius: "var(--mantine-radius-sm)",
                    backgroundColor: isActive
                      ? "var(--mantine-color-brand-light)"
                      : undefined,
                  }}
                >
                  <Text
                    size="sm"
                    c={isActive ? "brand" : undefined}
                    fw={isActive ? 500 : 400}
                  >
                    {preset.label}
                  </Text>
                </UnstyledButton>
              );
            })}
            {!required && (
              <>
                <Divider my="xs" />
                <UnstyledButton
                  onClick={handleClear}
                  px="xs"
                  py={6}
                  style={{
                    borderRadius: "var(--mantine-radius-sm)",
                  }}
                >
                  <Text size="sm" c="dimmed">
                    Clear / All Time
                  </Text>
                </UnstyledButton>
              </>
            )}
          </Stack>

          {/* Calendar column */}
          <Stack gap="xs" p="xs">
            <Text size="xs" c="dimmed" fw={500} px="xs">
              Custom Range
            </Text>
            <DatePicker
              type="range"
              value={pickerValue}
              onChange={handleDateChange}
              numberOfColumns={2}
              maxDate={new Date()}
            />
            {tempRange[0] && !tempRange[1] && (
              <Text size="xs" c="dimmed" ta="center">
                Select end date
              </Text>
            )}
          </Stack>
        </Group>
      </Popover.Dropdown>
    </Popover>
  );
};